// components/purchase-orders/PoCostQuestionDialog.jsx
//
// The cost question a PO save is answered with when a line's unit cost is not
// what the supplier charges now (usePurchaseOrderDraft `costQuestion`): one
// row per such item, from the supplier's cost now to the typed one, and
// whether the item cost moves with it (the supplier is the item's default).
//
// Three answers: save the order and the costs, save the order alone (the typed
// cost stays on its line), or close and go back to the form with nothing saved.

import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from '@mui/material';
import { useFormatters } from '../../hooks/useFormatters';
import { useHeldWhileClosed } from '../../hooks/useHeldWhileClosed';

function PoCostQuestionDialog({ question, supplierName, onAnswer }) {
  const { t } = useTranslation();
  const { formatCurrency } = useFormatters();
  const open = question !== null;
  // The rows stay on screen while the dialog fades out after an answer.
  const shown = useHeldWhileClosed(open, { question, supplierName });
  const changes = shown?.question.changes ?? [];
  const itemCostMoves = changes.some((row) => row.changes_item_cost);

  return (
    <Dialog
      className="dialog-form"
      open={open}
      onClose={open ? () => onAnswer(null) : undefined}
    >
      <DialogTitle>{t('purchaseOrders.create.costQuestion.title')}</DialogTitle>
      <DialogContent>
        {shown && (
          <Stack spacing={2}>
            <Typography variant="body2">
              {t('purchaseOrders.create.costQuestion.message', {
                count: changes.length, supplier: shown.supplierName,
              })}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('purchaseOrders.create.costQuestion.colItem')}</TableCell>
                    <TableCell align="right">{t('purchaseOrders.create.costQuestion.colFrom')}</TableCell>
                    <TableCell align="right">{t('purchaseOrders.create.costQuestion.colTo')}</TableCell>
                    <TableCell>{t('purchaseOrders.create.costQuestion.colItemCost')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changes.map((row) => (
                    <TableRow key={row.item_id}>
                      <TableCell>{row.item_label}</TableCell>
                      <TableCell align="right">
                        {row.from_cost == null ? '—' : formatCurrency(Number(row.from_cost), row.currency_id)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(Number(row.to_cost), row.currency_id)}
                      </TableCell>
                      <TableCell>
                        {row.changes_item_cost ? t('purchaseOrders.create.costQuestion.itemCostChanges') : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {itemCostMoves && (
              <Typography variant="body2" color="textSecondary">
                {t('purchaseOrders.create.costQuestion.itemCostNote')}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      {/* Disabled while fading out: the question is answered once. */}
      <DialogActions>
        <Button variant="text" onClick={() => onAnswer(null)} disabled={!open}>
          {t('btn.cancel')}
        </Button>
        <Button variant="outlined" onClick={() => onAnswer(true)} disabled={!open}>
          {t('purchaseOrders.create.costQuestion.saveAndUpdate')}
        </Button>
        {/* The order alone is the default, as "Just this PO" is in the
            lock-rate prompt: a merchant who did not think about the supplier's
            cost does not change it by pressing Enter. */}
        <Button variant="contained" onClick={() => onAnswer(false)} disabled={!open} autoFocus>
          {t('purchaseOrders.create.costQuestion.saveOrderOnly')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PoCostQuestionDialog;
